import { useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import type { Socket } from 'socket.io-client';
import type { IArgument } from '@newo/api/gateway';
import useAppStore from '@/store/app';
import { useCreateActor } from '@/services/customer/customer/customer.queries.ts';
import { getArgumentsDictionary } from '@/utils';

const API_URL = import.meta.env.VITE_CHAT_API;
const CLIENT_SECRET = import.meta.env.VITE_CLIENT_SECRET;

export type Command = {
    integration_idn: string;
    connector_idn: string;
    command_idn: string;
    act_id: string;
    external_event_id: string;
    arguments: IArgument[];
};

const useSocket = (): Socket | undefined => {
    const socketRef = useRef<Socket>(undefined);
    const externalId = useAppStore((state) => state.externalId);

    const { mutateAsync: createActor } = useCreateActor();
    useEffect(() => {
        if (!CLIENT_SECRET || !externalId) {
            return;
        }

        (async () => {
            await createActor({
                name: 'create-partner-organization-actor',
                external_id: externalId,
                client_secret: CLIENT_SECRET
            });
            if (!socketRef.current) {
                socketRef.current = io(API_URL, {
                    transports: ['websocket'],
                    autoConnect: true,
                    reconnection: true,
                    reconnectionAttempts: Infinity,
                    reconnectionDelay: 2000,
                    reconnectionDelayMax: 10000,
                    query: {
                        client_secret: CLIENT_SECRET,
                        origin: globalThis.origin,
                        external_id: externalId
                    }
                });

                const socket = socketRef.current;

                socket.on('command', (command: Command) => {
                    if (command.command_idn === 'onboarding_finished') {
                        const data = getArgumentsDictionary(command.arguments);
                        const payload = {
                            partnerOrganizationId: data.created_customer_idn,
                            shouldOpenPopup: true
                        };

                        globalThis.location.href = `${import.meta.env.VITE_WL_PORTAL_URL}/auth/login?partnerCode=${btoa(JSON.stringify(payload))}`;
                    }
                });
            }
        })();

        return () => {
            if (socketRef.current) {
                socketRef.current.removeAllListeners();
            }
        };
    }, []);

    return socketRef.current;
};

export default useSocket;
